import {
  BanUserSchema,
  ChangeUserStatusSchema,
  SuspendUserSchema,
  type TransitionableUserStatus,
} from '@aysar/contracts';
import { type TFunction } from 'i18next';

import { ChoiceCards } from '@/shared/components/selection/ChoiceCards/ChoiceCards';
import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import { DatePicker } from '@/shared/components/ui/DatePicker/DatePicker';
import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';

/*
 * The fields of the three account dialogs, in the kit since P27c1: the staff
 * page and the members page open the same dialogs, and a dialog may not import
 * a module (W2). Every key is `uiKit:account.*`.
 */

/**
 * A calendar date, as the END of that day in UTC.
 *
 * "Suspended until the 5th" means the account comes back on the 6th. Sending
 * midnight *starting* the 5th would lift the suspension a whole day early — a
 * silent off-by-one on an enforcement action, which is the worst place to have
 * one.
 */
function endOfDayIso(date: string): string {
  return `${date}T23:59:59.999Z`;
}

/** The date part of an ISO datetime, for prefilling the picker. */
function dateOf(value: unknown): string | null {
  return typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : null;
}

/**
 * Suspension is a FORM, not a confirm dialog.
 *
 * The api requires an end date and a reason, and the member is shown the reason.
 * A yes/no dialog cannot collect either, and a suspension whose reason was typed
 * carelessly into a confirm box is one the member reads verbatim.
 */
export function buildSuspendFields(t: TFunction): FieldDef[] {
  return [
    {
      name: 'until',
      kind: 'custom',
      label: t('account.suspend.until'),
      description: t('account.suspend.untilHint'),
      isRequired: true,
      render: (control) => (
        <DatePicker
          id={control.id}
          // A `custom` field has to place this itself — only the caller knows
          // which element is the control. Without it this field's hint and its
          // required-field error are announced to nobody.
          aria-describedby={control.describedById}
          value={dateOf(control.value)}
          isInvalid={control.isInvalid}
          disabled={control.isDisabled}
          onValueChange={(next) => {
            control.onChange(next === null ? undefined : endOfDayIso(next));
          }}
        />
      ),
    },
    {
      name: 'reason',
      kind: 'textarea',
      label: t('account.suspend.reason'),
      description: t('account.suspend.reasonHint'),
      isRequired: true,
      colSpan: 'full',
      maxLength: 1000,
    },
  ];
}

/**
 * The generic lifecycle move — everything suspend and ban are not.
 *
 * The target is **`ChoiceCards`, not a `<Select>`**: each reachable status
 * carries a consequence the operator has to weigh ("they can browse and be
 * matched" vs "they are told, and may submit again"), and a trigger that
 * collapses those hints hides exactly the thing being compared (P10.6e1). The
 * form vocabulary has no `choice` kind, so it arrives the same way the
 * `DatePicker` above does — as a `custom` field that names itself.
 *
 * `targets` comes from `statusTargets`, so a status the server would refuse is
 * never on screen to be picked.
 */
export function buildChangeStatusFields(
  t: TFunction,
  targets: readonly TransitionableUserStatus[],
): FieldDef[] {
  const options: SelectionOption[] = targets.map((status) => ({
    value: status,
    label: t(`account.status.${status}`),
    description: t(`account.changeStatus.option.${status}`),
  }));

  return [
    {
      name: 'status',
      kind: 'custom',
      label: t('account.changeStatus.status'),
      description: t('account.changeStatus.statusHint'),
      isRequired: true,
      colSpan: 'full',
      // `ChoiceCards` renders a real radio group with its own `aria-label`, so
      // the form must NOT print a `<Label htmlFor>` pointing at an element that
      // does not carry the id.
      isSelfNaming: true,
      render: (control) => (
        <ChoiceCards
          id={control.id}
          label={t('account.changeStatus.status')}
          aria-describedby={control.describedById}
          items={options}
          value={typeof control.value === 'string' ? control.value : null}
          isDisabled={control.isDisabled}
          onValueChange={control.onChange}
        />
      ),
    },
    {
      name: 'reason',
      kind: 'textarea',
      label: t('account.changeStatus.reason'),
      // Optional on the contract, but never a throwaway box: `statusReason` is
      // the human explanation shown to the MEMBER, and it stays on the account.
      description: t('account.changeStatus.reasonHint'),
      colSpan: 'full',
      maxLength: 1000,
      // **Load-bearing, not tidiness.** The kit seeds a text control with `''`
      // to keep it controlled, and `ChangeUserStatusSchema.reason` is
      // `.min(1).optional()` — so without this an untouched box submits
      // `reason: ''`, the resolver refuses, and Save is a dead button carrying
      // an error on a field nobody filled in. Every optional text field whose
      // contract has a minimum needs it.
      isEmptyAbsent: true,
    },
  ];
}

/** A ban is terminal and the reason is mandatory, for the same reason. */
export function buildBanFields(t: TFunction): FieldDef[] {
  return [
    {
      name: 'reason',
      kind: 'textarea',
      label: t('account.ban.reason'),
      description: t('account.ban.reasonHint'),
      isRequired: true,
      colSpan: 'full',
      maxLength: 1000,
    },
  ];
}

export { BanUserSchema, ChangeUserStatusSchema, SuspendUserSchema };
